import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { GetRFQ } from "../../../redux/actions/CryptoTransaction/Liquidity";

const SAFETY_BUFFER_SECONDS = 0;

export const useCryptoRFQ = ({
  cryptoCurrency,
  fiatCurrency,
  side, // 'buy' or 'sell'
  amount,
  inputMode = "fiat", // 'fiat' or 'crypto'
  cryptoAccountId,
  fiatAccountId,
  debounceMs = 500,
  setError,
  error,
  handleBack = () => {}
}) => {
  const dispatch = useDispatch();
  const requestIdRef = useRef(0);

  const [rfq, setRfq] = useState(null);
  const [quoteId, setQuoteId] = useState(null);

  // Dedicated states for response quantities
  const [fromAmount, setFromAmount] = useState(null);
  const [toAmount, setToAmount] = useState(null);

  const [expiresAt, setExpiresAt] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);

  const [loading, setLoading] = useState(false);

  const reset = () => {
    setRfq(null);
    setQuoteId(null);
    setFromAmount(null);
    setToAmount(null);
    setExpiresAt(null);
    setTimeLeft(0);
    setTotalDuration(0);
    setError(null);
  };

  const fetchQuote = useCallback(async () => {
    if (!cryptoCurrency || !fiatCurrency || !cryptoAccountId || !fiatAccountId || !amount || Number(amount) <= 0) {
      reset();
      return;
    }

    const requestId = ++requestIdRef.current;

    setLoading(true);
    setError(null);

    try {
      const isBuy = side.toLowerCase() === "buy";
      
      // Determine request payload key based on side & inputMode:
      // BUY:  from_amount = Fiat (mode === 'fiat'),  to_amount = Crypto (mode === 'crypto')
      // SELL: from_amount = Crypto (mode === 'crypto'), to_amount = Fiat (mode === 'fiat')
      let amountPayload = {};

      if (isBuy) {
        if (inputMode === "fiat") {
          amountPayload = { from_amount: amount };
        } else {
          amountPayload = { to_amount: amount };
        }
      } else { // SELL
        if (inputMode === "crypto") {
          amountPayload = { from_amount: amount };
        } else {
          amountPayload = { to_amount: amount };
        }
      }

      const payload = {
        instrument: `${cryptoCurrency}-${fiatCurrency}`,
        side: side.toLowerCase(),
        ...amountPayload,
        from_account_id: String(isBuy ? fiatAccountId : cryptoAccountId),
        to_account_id: String(isBuy ? cryptoAccountId : fiatAccountId),
      };

      const response = await dispatch(GetRFQ(payload));

      // Ignore stale responses
      if (requestId !== requestIdRef.current) {
        return;
      }

      if (!response?.success || !response?.data) {
        reset();
        setError(response?.message || "Something went wrong. Please try again.");
        handleBack();
        return;
      }

      const data = response.data;
      const validUntilMs = new Date(data.valid_until).getTime();
      const createdMs = data.created ? new Date(data.created).getTime() : Date.now();
      const duration = Math.max(1, Math.ceil((validUntilMs - createdMs) / 1000));

      setRfq(response);
      setQuoteId(data.rfq_id);

      // Extract and set from_amount & to_amount from API response data
      setFromAmount(data.from_amount ?? null);
      setToAmount(data.to_amount ?? null);

      setExpiresAt(validUntilMs);
      setTotalDuration(duration);
    } catch (err) {
      console.error(err);
      reset();
      setError(err?.message || "Failed to fetch quote.");
      handleBack();
    } finally {
      setLoading(false);
    }

    // eslint-disable-next-line
  }, [dispatch, cryptoCurrency, fiatCurrency, side, amount, inputMode, cryptoAccountId, fiatAccountId]);

  /**
   * Debounce RFQ creation
  */
  useEffect(() => {
    const timeout = setTimeout(fetchQuote, debounceMs);
    return () => clearTimeout(timeout);
  }, [fetchQuote, debounceMs]);

  /**
   * Countdown timer logic
  */
  useEffect(() => {
    if (!expiresAt) {
      setTimeLeft(0);
      return;
    }

    const updateCountdown = () => {
      const remainingMs = expiresAt - Date.now();
      const remainingSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
      setTimeLeft(remainingSeconds);
    };

    updateCountdown();

    const interval = setInterval(updateCountdown, 250);
    return () => clearInterval(interval);
  }, [expiresAt]);

  const isExpired = !expiresAt || expiresAt - Date.now() <= SAFETY_BUFFER_SECONDS * 1000;

  return {
    rfq,
    quoteId,
    fromAmount,
    toAmount,
    timeLeft,
    totalDuration,
    expiresAt,
    isExpired,
    loading,
    error,
    refetch: fetchQuote,
  };
};